import { MemulRealtorInfoType } from '@/app/(realtor)/realtor/type';
import { chartType } from '@/service/chart/chart';
import { customFetch } from '@/service/commonFetch';
import { MemulCheckType, type MemulDetailType } from '@/service/memul/memulDetailType';
import { MemulRealPriceItemType, realPricePramsType } from '@/service/memul/memulRealPrice';
import { distanceSearchListType } from '@/service/surroundingEnv/distanceSearchListType';

export const queryKeys = {
  memulSummary: (articleCd: string) => ['memulSummary', articleCd],
  realtorInfo: (articleCd: string) => ['memulRealtorInfo', articleCd],
  memulDetailChart: (params: chartType) => ['memulDetailChart', params],
  memulRealPrice: (params: realPricePramsType) => ['memulRealPrice', params],
  memulDetail: (articleCd: string) => ['memulDetail', articleCd],
  memulCheck: (articleCd: string) => ['memulCheck', articleCd],
  surroundingEnv: (articleCd: string) => ['surroundingEnv', articleCd],
  distanceSearchList: (params: distanceSearchListType) => ['distanceSearchList', params],
  distanceSearchTab: (params: distanceSearchListType) => ['distanceSearchTab', params],
  similarMemulsRecommend: (params: string) => ['memulFavorite', 'similarMemulsRecommend', params],
  similarMemulsConversion: (params: string) => ['memulFavorite', 'similarMemulsConversion', params],
};

const queries = {
  // *** 매물상세 ***
  /** @description 요약정보 */
  memulSummary: (articleCd: string) => ({
    queryKey: queryKeys.memulSummary(articleCd),
    queryFn: () =>
      customFetch<any>(`/offerings/detail/summary`, {
        params: { articleCd },
        next: {
          revalidate: 60,
          tags: queryKeys.memulSummary(articleCd),
        },
      }),
  }),
  /** @description 중개업소 정보 */
  realtorInfo: (articleCd: string) => ({
    queryKey: queryKeys.realtorInfo(articleCd),
    queryFn: () =>
      customFetch<MemulRealtorInfoType>(`/offerings/detail/realtor`, {
        params: { articleCd },
        next: {
          revalidate: 60,
          tags: queryKeys.realtorInfo(articleCd),
        },
      }),
  }),

  /** @description 동일면적 실거래 차트 */
  memulDetailChart: (params: chartType) => ({
    queryKey: queryKeys.memulDetailChart(params),
    queryFn: () =>
      customFetch<any>(`/offerings/trade`, {
        params: { ...params },
        next: {
          revalidate: 60,
          tags: queryKeys.memulDetailChart(params) as string[],
        },
      }),
  }),

  /** @description 동일면적 실거래 상세 */
  memulRealPrice: (params: realPricePramsType) => ({
    queryKey: queryKeys.memulRealPrice(params),
    queryFn: ({ pageParam }: { pageParam: number }) =>
      customFetch<MemulRealPriceItemType>(`/complex/trade/real-price`, {
        params: { page: pageParam, ...params },
      }),
  }),

  /** @description 매물정보 */
  memulDetail: (articleCd: string) => ({
    queryKey: queryKeys.memulDetail(articleCd),
    queryFn: () =>
      customFetch<MemulDetailType>(`/offerings/detail`, {
        params: { articleCd },
        next: {
          revalidate: 60,
          tags: queryKeys.memulDetail(articleCd),
        },
      }),
  }),
  memulCheck: (articleCd: string) => ({
    queryKey: queryKeys.memulCheck(articleCd),
    queryFn: () =>
      customFetch<MemulCheckType>(`/offerings/check`, {
        params: { articleCd },
        next: {
          revalidate: 60,
          tags: queryKeys.memulCheck(articleCd),
        },
      }),
  }),

  /** @description 주변환경 */
  surroundingEnv: (articleCd: string) => ({
    queryKey: queryKeys.surroundingEnv(articleCd),
    queryFn: () =>
      customFetch<any>(`/offerings/detail/nearby/places`, {
        params: { articleCd },
        next: {
          revalidate: 60,
          tags: queryKeys.surroundingEnv(articleCd),
        },
      }),
  }),

  /** @description 내 직장/학교와 거리는? 검색리스트 */
  distanceSearchList: (params: distanceSearchListType) => ({
    queryKey: queryKeys.distanceSearchList(params),
    queryFn: ({ pageParam }: { pageParam?: number }) =>
      customFetch<any>(`/offerings/detail/distance`, {
        params: { page: pageParam, ...params },
      }),
  }),

  /** @description 유사 매물 - 매물기준 1000m 안에 있는 매물 */
  similarMemulsRecommend: (articleCd: string) => ({
    queryKey: queryKeys.similarMemulsRecommend(articleCd),
    queryFn: () =>
      customFetch<any>(`/offerings/recommended`, {
        params: { articleCd },
        next: {
          revalidate: 60,
          tags: queryKeys.similarMemulsRecommend(articleCd),
        },
      }),
  }),

  /** @description 유사 매물 - 전/월세 전환 매물리스트 */
  similarMemulsConversion: (articleCd: string) => ({
    queryKey: queryKeys.similarMemulsConversion(articleCd),
    queryFn: () =>
      customFetch<any>(`/offerings/recommended/conversion`, {
        params: { articleCd },
        next: {
          revalidate: 60,
          tags: queryKeys.similarMemulsConversion(articleCd),
        },
      }),
  }),
};

export default queries;
